import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';

import isEqual from 'lodash/isEqual';
import { useState, useCallback, useEffect } from 'react';
// @mui
import { useSnackbar } from 'src/components/snackbar';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { denseHeight } from 'src/constants/TableRowHeight';
import { RouterLink } from 'src/routes/components';
// _mock
import { _userList, _roles, USER_STATUS_OPTIONS } from 'src/_mock';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import BreadCrumbs from 'src/components/custom-breadcrumbs/breadcrumbs';
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';
//
import axiosInstance from 'src/api/axiosInstance';
// import UserTableToolbar from '../user/user-table-toolbar';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, setPreservedFilters } from 'src/redux/reducers/filtersReducer';

import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import useNavigateErrorHandler from 'src/hooks/useNavigateErrorHandler';
import { Grid } from '@mui/material';

import { useLocation } from 'react-router';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
import UserTableFiltersResult from '../user/user-table-filters-result';
import MstTableRow from './SubscrSiteTable';
import MstQuickEditForm from './SubScrSiteMst';
import CommonFilterResult from './CommonFilter/CommonFilterResult';
import CommonFilter from './CommonFilter/CommonFilter';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...USER_STATUS_OPTIONS];

const TABLE_HEAD = [
  { id: 'mst', label: 'Subscriber Site  ' },
  { id: 'country', label: 'Country' },
  { id: 'state', label: 'State' },
  { id: 'city', label: 'City' },
  { id: 'actions', label: 'Actions', width: 100, align: 'center' },
];

const defaultFilters = {
  Status: [],
  SubscriberSite: [],
  City: [],
  State: [],
  Country: [],
};

// ----------------------------------------------------------------------

export default function MstListView({ fullAccess }) {
  const token = sessionStorage.getItem('token');
  const table = useTable();
  const settings = useSettingsContext();
  const router = useRouter();
  const confirm = useBoolean();
  const quickEdit = useBoolean();
  const { enqueueSnackbar } = useSnackbar();
  // Declare a separate state for bulk delete confirmation
  const confirmBulkDelete = useBoolean();

  // Define the table headers for specific columns
  const customTableHeaders = {
    mst: 'Subscriber Site ',
    country: 'Country',
    state: 'State',
    city: 'City',
  };

  const openFilters = useBoolean();

  const handleApiError = useNavigateErrorHandler();


  // Define the function to generate the Excel file
  async function generateExcelFile(data) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Subscriber Site ');

    let filteredData = data;
    if (table.selected.length > 0) {
      filteredData = data.filter((item) => table.selected.includes(item.id));
    }

    // Set up the header row
    const headerRow = worksheet.addRow(Object.values(customTableHeaders));
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center' };
    headerRow.eachCell((cell) => {
      if (cell.value) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCCE5FF' },
        };
      }
    });

    // Add data rows
    filteredData.forEach((row) => {
      const dataRow = worksheet.addRow([]);
      // Use the customTableHeaders object to map column names to header labels
      dataRow.getCell(1).value = row.mst || '-';
      dataRow.getCell(2).value = row?.country?.countryName || '-';
      dataRow.getCell(3).value = row?.state?.stateName || '-';
      dataRow.getCell(4).value = row?.city?.cityName || '-';

      const alignmentOptions = {
        horizontal: 'left',
        vertical: 'center',
        wrapText: true,
      };

      dataRow.eachCell((cell, colNumber) => {
        cell.alignment = alignmentOptions;
      });
    });

    // Set up the workbook properties
    // worksheet.properties.defaultRowHeight = 18;
    worksheet.properties.defaultColWidth = 20;

    // Save the workbook as a blob
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Save the blob as a file
    saveAs(blob, 'Subscriber Site .xlsx');
  }

  const [tableData, setTableData] = useState([]);

  const today = new Date();

  const getArrData = async () => {
    await axiosInstance
      .get(`/rest/subscrsite/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        // eslint-disable-next-line arrow-body-style
        const mod_data = res.data.map((pt) => {
          const end = pt.endDate ? new Date(pt.endDate) : null;
          return {
            id: pt.subscrSiteId,
            mst: pt.subscrSiteName,
            country: pt?.subscrCountry || null,
            state: pt?.subscrState || null,
            city: pt?.subscrCity || null,
            end: pt.endDate,
            start: new Date(pt.startDate),
            lastUpdtDate: new Date(pt.lastUpdatedDate),
            status: pt.status || (end && end < today ? "Inactive" : "Active"),
          }
        });
        mod_data.sort((a, b) => new Date(a.lastUpdtDate) - new Date(b.lastUpdtDate));
        setTableData(mod_data);
        // console.table("Subscriber Site  data ============>", res.data);
      })
      .catch((err) => {
        console.error(err);
        handleApiError(err);
      });
  };

  useEffect(() => {
    getArrData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const dispatch = useDispatch();
  const location = useLocation();
  const preservedFilters = useSelector(
    (state) => state.filters.preservedFilters[location.pathname]
  );

  const [filters, setFiltersState] = useState(preservedFilters || defaultFilters);
  const [searchQuery, setSearchQuery] = useState('');


  const handleSearch = useCallback(
    (event) => {
      const { value } = event.target;
      setSearchQuery(value);
      table.onResetPage(); // Reset pagination
    },
    [table]
  );

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters: {
      ...filters,
      name: searchQuery,
      country: searchQuery,
      state: searchQuery,
      city: searchQuery,
    },
  });

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  
  const canReset = !isEqual(defaultFilters, filters);
  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;


  useEffect(() => {
    // const currentFilters = preservedFilters || { ...defaultFilters, status: ['Active'] };
    // setFiltersState(currentFilters);
    if (filters.Status.length === 0) {
      handleFilters('Status', ['Active']); // Default filter for "Experience To"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {

    const currentFilters = preservedFilters || { ...defaultFilters };
    setFiltersState(currentFilters);
    dispatch(setFilters(currentFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    // Set filters from Redux if they exist
    if (preservedFilters && Object.keys(preservedFilters).length > 0) {
      setFiltersState(preservedFilters);
    }
  }, [preservedFilters]);

  const { onResetPage } = table; // Destructure first

  useEffect(() => {
    onResetPage(); // Call the function
  }, [filters, onResetPage]); // Only track function and filter changes



  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = {
        ...filters,
        [name]: value,
      };
      setFiltersState(newFilters);
      dispatch(setPreservedFilters({ path: location.pathname, filters: newFilters }));
    },
    [dispatch, location.pathname, filters]
  );

  const handleResetFilters = useCallback(() => {
    const defaultState = { ...defaultFilters };
    setFiltersState(defaultState);
    dispatch(setPreservedFilters({ path: location.pathname, filters: defaultState }));
  }, [dispatch, location.pathname]);

  const user = JSON.parse(localStorage.getItem('user'));
  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));

  const handleDeleteRow = useCallback(
    async (id) => {

      const encryptedId = await fetchEncryptedToken(id, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      try {
        const response = await axiosInstance.put(
          `rest/subscrsite/delete/${encryptedId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        enqueueSnackbar('Subscriber Site Deleted!');
        // Remove the deleted row from table data

        const updatedTableData = tableData.map((row) =>
          row.id === id ? { ...row, status: 'Inactive', end: new Date() } : row
        );

        setTableData(updatedTableData);
        // Update the table page after deletion

        // Notify table component of changes
        table.onUpdatePageDeleteRows({
          totalRows: updatedTableData.length,
          totalRowsInPage: dataInPage.length,
          totalRowsFiltered: dataFiltered.length,
        });

        // Clear selected rows
        table.setSelected([]);
      } catch (error) {
        enqueueSnackbar('Error deleting interview level!', { variant: 'error' });
        handleApiError(error);
      }
    },
    [
      enqueueSnackbar,
      dataInPage.length,
      table,
      tableData,
      token,
      handleApiError,
      dataFiltered.length,
    ]
  );

  const handleDeleteRows = useCallback(() => {
    try {
      const deleteRows = tableData.filter((row) => !table.selected.includes(row.id));
      setTableData(deleteRows);

      table.onUpdatePageDeleteRows({
        totalRows: tableData.length,
        totalRowsInPage: dataInPage.length,
        totalRowsFiltered: dataFiltered.length,
      });
    } catch (error) {
      console.error('Error deleting multiple rows:', error);
      enqueueSnackbar('Error deleting multipl ', { variant: 'error' });
      handleApiError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFiltered.length, dataInPage.length, enqueueSnackbar, table, tableData]);


  const handleEditRow = useCallback(
    async (id) => {
      const encryptedId = await fetchEncryptedToken(id, token);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      router.push(paths.dashboard.user.edit(encryptedId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router]
  );

  const handleFilterStatus = useCallback(
    (event, newValue) => {
      handleFilters('status', newValue);
    },
    [handleFilters]
  );

  const handleBulkDelete = async () => {
    try {
      const selectedIds = table.selected.map((rowId) => {
        const rowData = tableData.find((row) => row.id === rowId);
        return rowData?.id;
      });

      const filteredIds = selectedIds.filter(Boolean);

      if (filteredIds.length === 0) {
        console.warn('No valid IDs to delete.');
        return;
      }

      const url = `/rest/subscrsite/bulkDelete?lastUpdatedBy=${user.userId
        }&subscrSiteId=${subscr.subscrSiteId}`;


      // Set up the configuration object with the headers
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Make the axiosInstance request with filteredIds as the data payload and the configuration object
      await axiosInstance.put(url, filteredIds, config);

      // Update the table data to mark rows as "Inactive"
      const updatedTableData = tableData.map((row) =>
        filteredIds.includes(row.id) ? { ...row, status: 'Inactive', end: new Date() } : row
      );

      setTableData(updatedTableData);

      enqueueSnackbar('Subscriber Sites Deleted!');
      table.onUpdatePageDeleteRows({
        totalRows: updatedTableData.length,
        totalRowsInPage: dataInPage.length,
        totalRowsFiltered: dataFiltered.length,
      });

      // Reset the selected rows after successful bulk delete
      table.setSelected([]);
    } catch (error) {
      enqueueSnackbar('Error deleting bulk interview levels!', { variant: 'error' });
      console.error('Error deleting items:', error);
      // handleApiError(error);
    }
  };

  // Function to handle bulk delete confirmation
  const handleBulkDeleteConfirmation = () => {
    confirmBulkDelete.onTrue(); // Show the confirmation dialog
  };

  const filterSections = [
    {
      title: 'Status',
      options: ['Active', 'Inactive'],
      filterKey: 'Status',
    },
    {
      title: 'Subscriber Site',
      options: [...new Set(tableData.map((item) => item.mst))],
      filterKey: 'SubscriberSite',
    },
    {
      title: 'City',
      options: [
        ...new Set(
          tableData
            .map((item) => (item.city ? item.city.cityName : '')) // Check if item.city exists
            .filter(Boolean) // Remove null, undefined, or empty strings
        ),
      ],

      filterKey: 'City',
    },
    {
      title: 'State',
      options: [
        ...new Set(
          tableData.map((item) => (item.state ? item.state.stateName : '')).filter(Boolean)
        ),
      ],
      filterKey: 'State',
    },
    {
      title: 'Country',
      options: [
        ...new Set(
          tableData.map((item) => (item.country ? item.country.countryName : '')).filter(Boolean)
        ),
      ],
      filterKey: 'Country',
    },
  ];

  useEffect(() => {
    // Set default filters here if they are not already set
    if (filters.Status.length === 0) {
      handleFilters('Status', ['Active']); // Default filter for "Experience To"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <>
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <BreadCrumbs
          heading={
            <>
              Subscriber Site
              <Tooltip
                title="A Subscriber Site refers to a specific location or office where the user will be working or assigned. This can include physical locations like a or remote site. It helps in identifying the operational area or team base for the user.e.g.Mumbai, Pune, etc."
                placement="right"
                arrow
              >
                <Iconify icon="ep:info-filled" style={{ width: '30px' }} />
              </Tooltip>
            </>
          }
          action={
            <>
              {fullAccess && (
                <>
                  <Button
                    color="secondary"
                    variant="soft"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={quickEdit.onTrue}
                  >
                    New SubScriber Site
                  </Button>
                  <MstQuickEditForm
                    open={quickEdit.value}
                    onClose={quickEdit.onFalse}
                    getArrData={getArrData}
                    tableData={tableData}
                  />
                </>
              )}
            </>
          }
          sx={{
            mb: { xs: 1, md: 1 },
          }}
        />
        <Grid container alignItems="center" justifyContent="flex-end" sx={{ marginBottom: '4px' }}>
          <Grid item>
            <Stack direction="row" alignItems="center" justifyContent="flex-start">
              <Tooltip title="Download Excel" placement="top" arrow>
                <IconButton onClick={() => generateExcelFile(dataFiltered)}>
                  <Iconify icon="vscode-icons:file-type-excel" width={30} height={30} />
                </IconButton>
              </Tooltip>

              <CommonFilter
                open={openFilters.value}
                onOpen={openFilters.onTrue}
                onClose={openFilters.onFalse}
                filters={filters || defaultFilters}
                onFilters={handleFilters}
                canReset={canReset}
                onResetFilters={handleResetFilters}
                filterSections={filterSections}
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid container alignItems="center">
          <Grid item>
            {canReset && (
              <CommonFilterResult
                filters={filters || defaultFilters}
                onFilters={handleFilters}
                //
                canReset={canReset}
                onResetFilters={handleResetFilters}
                //
                results={dataFiltered.length}
                filterSections={filterSections}
              />
            )}
          </Grid>
        </Grid>
        <Card>
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={dataFiltered.length}
              onSelectAllRows={(checked) =>
                table.onSelectAllRows(
                  checked,
                  dataFiltered.map((row) => row.id)
                )
              }
              action={
                fullAccess ? (
                  <Tooltip title="Delete" placement="top" arrow>
                    <IconButton color="error" onClick={handleBulkDeleteConfirmation}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  </Tooltip>
                ) : null
              }
            />

            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 900 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={dataFiltered.length}
                  numSelected={table.selected.length}
                  onSort={table.onSort}
                  onSelectAllRows={(checked) =>
                    table.onSelectAllRows(
                      checked,
                      dataFiltered.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {dataFiltered
                    .slice(
                      table.page * table.rowsPerPage,
                      table.page * table.rowsPerPage + table.rowsPerPage
                    )
                    .map((row) => (
                      <MstTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                        onDeleteRow={() => handleDeleteRow(row.id)}
                        onEditRow={() => handleEditRow(row.id)}
                        getArrData={getArrData}
                        numselected={table.selected.length}
                        fullAccess={fullAccess}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(table.page, table.rowsPerPage, dataFiltered.length)}
                  />

                  <TableNoData notFound={notFound} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={dataFiltered.length}
            page={table.page}
            rowsPerPage={table.rowsPerPage}
            onPageChange={table.onChangePage}
            onRowsPerPageChange={table.onChangeRowsPerPage}
            //
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>
      </Container>

      {/*   // Multiple Delete (Bulk Delete) Confirmation Dialog */}
      <ConfirmDialog
        open={confirmBulkDelete.value}
        onClose={confirmBulkDelete.onFalse}
        title="Delete Subscriber Site"
        content={
          <>
            Are you sure you want to delete <strong>{table.selected.length}</strong> Subscriber Sites?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleBulkDelete(); // Handle bulk delete operation
              confirmBulkDelete.onFalse(); // Close the bulk delete confirmation dialog
            }}
          >
            Delete
          </Button>
        }
      />

      {/*  // Single Row Delete */}
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Subscriber Site"
        content={
          <>
            Are you sure you want to delete <strong> {table.selected.length} </strong> Sub
            scriber Site?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDeleteRows();
              confirm.onFalse();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}

MstListView.propTypes = {
  fullAccess: PropTypes.bool,
};
// ----------------------------------------------------------------------

function applyFilter({ inputData, comparator, filters }) {
  const { Status, SubscriberSite, Country, State, City } = filters;
  const today = new Date(); // Get today's date
  // console.log('Status filter applied:', SubscriberSite);

  // Ensure all items have a default "Active" status if not already set
  inputData = inputData.map((item) => ({
    ...item,
    status: item.status || 'Active', // Default to "Active" if undefined
  }));

  // Stabilize the array with indices for sorting
  const stabilizedThis = inputData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1]; // Maintain original order when comparator returns 0
  });

  // Extract sorted data from stabilized array
  inputData = stabilizedThis.map((el) => el[0]);

  // Apply Status filter (Active/Inactive)
  if (Status && Status.length > 0) {
    inputData = inputData.filter((item) => {
      const isActive = item.status === 'Active';
      const isInactive = item.status === 'Inactive';

      if (Status.includes('Active') && isActive) {
        return true;
      }

      if (Status.includes('Inactive') && isInactive) {
        return true;
      }

      return false;
    });
  }
  // console.log('Subscriber Site  filter applied:', SubscriberSite);

  if (SubscriberSite && SubscriberSite.length > 0) {
    inputData = inputData.filter((item) =>
      SubscriberSite.some((name) => item.mst?.toLowerCase().trim() === name?.toLowerCase().trim())
    );
  }

  if (City && City.length > 0) {
    inputData = inputData.filter((itemCategory) => {
      const cityName = itemCategory.city?.cityName || ''; // Default to 'N/A' if cityName is null or undefined
      return City.includes(cityName); // Check if City includes the value
    });
  }

  if (State && State.length > 0) {
    inputData = inputData.filter((itemCategory) => {
      const stateName = itemCategory.state?.stateName || ''; // Default to 'N/A' if stateName is null or undefined
      return State.includes(stateName); // Check if State includes the value
    });
  }

  if (Country && Country.length > 0) {
    inputData = inputData.filter((itemCategory) => {
      const countryName = itemCategory.country?.countryName || ''; // Handle null or undefined values
      return Country.includes(countryName);
    });
  }

  return inputData;
}

